import { DocIdParams, DocMetaV1, DocumentV1 } from '../dto/documents.js';
import { listOf, PageQueryV1 } from '../dto/list.js';
import { RestoreRevisionRequestV1, RevisionParams, RevisionSummaryV1, RevisionV1, TrashItemV1, } from '../dto/revisions.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_RESTORED_DOC, EXAMPLE_REVISION, EXAMPLE_REVISION_SUMMARY, EXAMPLE_TRASH_ITEM, EXAMPLE_TRASHED_META, EXAMPLE_UNTRASHED_META, } from './examples-history.js';
/**
 * A document's history and the Trash (1.0b deliverable 17), built by 1.A: the revision routes in
 * its stage 1b, the trash routes in its stage 2.
 */
const salt = EXAMPLE_IDS.questSaltInTheWells.id;
/** A document's revisions, newest first (`?cursor&limit`). Reference documents' too. */
export const docsRevisions = defineRoute({
    id: 'docs.revisions',
    method: 'GET',
    path: '/api/docs/:id/revisions',
    params: DocIdParams,
    query: PageQueryV1,
    response: { kind: 'json', schema: listOf(RevisionSummaryV1) },
    auth: 'session',
    reference: 'read',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.A',
    stage: '1b',
    since: 'exit-1a',
    examples: {
        request: { params: { id: salt }, query: { limit: 20 } },
        response: { items: [EXAMPLE_REVISION_SUMMARY], next: null },
    },
});
/** One revision whole. A missing document or revision answers 404. */
export const docsRevision = defineRoute({
    id: 'docs.revision',
    method: 'GET',
    path: '/api/docs/:id/revisions/:revNo',
    params: RevisionParams,
    response: { kind: 'json', schema: RevisionV1 },
    auth: 'session',
    reference: 'read',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.A',
    stage: '1b',
    since: 'exit-1a',
    examples: { request: { params: { id: salt, revNo: 1 } }, response: EXAMPLE_REVISION },
});
/**
 * Restore a revision's page text (only the text, whatever the revision's reason: DECISIONS entry
 * 38 item 2). A stale `baseBodyVersion` answers 409 `version_conflict` (details
 * `VersionConflictDetailsV1`, `conflictRevNo: null`); a trashed document 409 `document_trashed`.
 */
export const docsRestore = defineRoute({
    id: 'docs.restore',
    method: 'POST',
    path: '/api/docs/:id/revisions/:revNo/restore',
    params: RevisionParams,
    body: RestoreRevisionRequestV1,
    response: { kind: 'json', schema: DocumentV1 },
    errors: { 409: ['version_conflict', 'document_trashed'] },
    auth: 'session',
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.A',
    stage: '1b',
    since: 'exit-1a',
    examples: {
        request: { params: { id: salt, revNo: 1 }, body: { baseBodyVersion: 2 } },
        response: EXAMPLE_RESTORED_DOC,
        consumes: true,
    },
});
/** Move a document to the Trash. One already there answers 409 `document_trashed`. */
export const docsTrash = defineRoute({
    id: 'docs.trash',
    method: 'POST',
    path: '/api/docs/:id/trash',
    params: DocIdParams,
    response: { kind: 'json', schema: DocMetaV1 },
    errors: { 409: ['document_trashed'] },
    auth: 'session',
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.A',
    stage: '2',
    since: 'exit-1a',
    examples: {
        request: { params: { id: EXAMPLE_IDS.noteHarbourGossip.id } },
        response: EXAMPLE_TRASHED_META,
        consumes: true,
    },
});
/** Bring a document back from the Trash. A live or merged one answers 409 `not_in_trash`. */
export const docsUntrash = defineRoute({
    id: 'docs.untrash',
    method: 'POST',
    path: '/api/docs/:id/untrash',
    params: DocIdParams,
    response: { kind: 'json', schema: DocMetaV1 },
    errors: { 409: ['not_in_trash'] },
    auth: 'session',
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.A',
    stage: '2',
    since: 'exit-1a',
    examples: {
        request: { params: { id: EXAMPLE_IDS.noteTornLedger.id } },
        response: EXAMPLE_UNTRASHED_META,
        consumes: true,
    },
});
/** The trashed canon documents that are not merged, latest first. */
export const trashList = defineRoute({
    id: 'trash.list',
    method: 'GET',
    path: '/api/trash',
    query: PageQueryV1,
    response: { kind: 'json', schema: listOf(TrashItemV1) },
    auth: 'session',
    reference: 'none',
    status: 'frozen',
    owner: '1.A',
    stage: '2',
    since: 'exit-1a',
    examples: {
        request: { query: { limit: 50 } },
        response: { items: [EXAMPLE_TRASH_ITEM], next: null },
    },
});
//# sourceMappingURL=history.js.map